import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import FormFillContent from "../forms/FormFillContent";
import PublicRespondentGate from "../../components/forms/PublicRespondentGate";
// Icon font for the branded header/detail icons -- imported here too, not
// only by the internal Sidebar, because this route renders without it.
import "bootstrap-icons/font/bootstrap-icons.css";
import "./PublicFormPage.css";

// Phase 16 (Public Form Links), §22/§23/§25. The top-level route for
// /form/f/:token — deliberately OUTSIDE ProtectedRoute/AdminLayout/Layout/
// the sidebar/role-based shell (see routes/AppRoutes.js: this route is
// registered in the same "Public" group as /login, never nested under a
// PrivateRoute-wrapped layout route). Works fully logged out, in
// incognito, on mobile — no redirect to /login is ever possible from here.
//
// Public Form Respondent Identity: this page no longer pre-fetches
// GET /public/form-access/:token itself. The FIRST thing shown is
// PublicRespondentGate (email → POST .../verify-email) — a valid/invalid/
// expired/revoked/unavailable token is detected there too (the same
// resolveToken middleware runs on every route, verify-email included), via
// `onLinkError`. Only once the gate's onVerified fires does this page mount
// FormFillContent at all, which then does its OWN normal self-fetch of the
// form structure (no `initialRuntime` seed anymore — there is nothing to
// seed it with pre-verification). This also means the assignee's name is
// never visible to an unverified visitor: it used to ride along on the
// very first GET response before any identity check existed at all.
const LINK_STATE_COPY = {
  PUBLIC_LINK_INVALID: {
    title: "Link Invalid",
    message: "This form link is invalid or no longer available.",
  },
  PUBLIC_LINK_EXPIRED: {
    title: "Link Expired",
    message: "This form link has expired. Please contact the person who assigned the form.",
  },
  PUBLIC_LINK_REVOKED: {
    title: "Link Revoked",
    message: "This form link is no longer active.",
  },
  PUBLIC_LINK_UNAVAILABLE: {
    title: "Form Not Available",
    message: "This form is no longer available for submissions.",
  },
};
const DEFAULT_LINK_STATE = { title: "Link Invalid", message: "This form link is invalid or no longer available." };

export default function PublicFormPage() {
  const { token } = useParams();
  const [status, setStatus] = useState("gate"); // gate | form | error
  const [errorState, setErrorState] = useState(null);
  const [respondent, setRespondent] = useState(null); // { userId, name, email } from a verified /verify-email call

  // Referrer-Policy: no-referrer (§26) -- keeps this page's URL (which does
  // not itself contain the token, but a linked-out click still shouldn't
  // leak it) out of any outbound Referer header. Set here rather than only
  // via a server header so it applies even if the page is opened as a
  // static/cached response. Unconditional on mount now -- no longer tied
  // to a page-owned fetch (that fetch moved into PublicRespondentGate/
  // FormFillContent).
  useEffect(() => {
    let meta = document.querySelector('meta[name="referrer"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "referrer";
      document.head.appendChild(meta);
    }
    const previousContent = meta.content;
    meta.content = "no-referrer";
    return () => {
      meta.content = previousContent;
    };
  }, []);

  const handleLinkError = (code, message) => {
    setErrorState({ ...(LINK_STATE_COPY[code] || DEFAULT_LINK_STATE), message: message || (LINK_STATE_COPY[code] || DEFAULT_LINK_STATE).message });
    setStatus("error");
  };

  const handleVerified = (identity) => {
    setRespondent(identity);
    setStatus("form");
  };

  return (
    <div className="pfp-page">
      <header className="pfp-topbar">
        <div className="pfp-wordmark" aria-label="Jaipuria Schools">
          <span className="pfp-wordmark-name">JAIPURIA</span>
          <span className="pfp-wordmark-sub">SCHOOLS</span>
        </div>
        <span className="pfp-divider" aria-hidden="true" />
        <span className="pfp-product">IT360 Desk</span>
        <span className="pfp-divider pfp-divider-soft" aria-hidden="true" />
        <span className="pfp-brand">Ticketing System</span>
        {respondent?.name && (
          <span className="pfp-avatar" title={respondent.name} aria-label={`Signed in as ${respondent.name}`}>
            {respondent.name.trim().charAt(0).toUpperCase()}
          </span>
        )}
      </header>

      <div className="pfp-body">
        {status === "gate" && (
          <PublicRespondentGate token={token} onVerified={handleVerified} onLinkError={handleLinkError} />
        )}

        {status === "error" && (
          <div className="pfp-state-card pfp-error">
            <h3>{errorState.title}</h3>
            <p>{errorState.message}</p>
          </div>
        )}

        {status === "form" && (
          <FormFillContent mode="public" token={token} respondentEmail={respondent?.email} respondentName={respondent?.name} />
        )}
      </div>
    </div>
  );
}
